import React, { useMemo, useState, useEffect, useCallback } from 'react'
import useCashierProducts from '../../hooks/useCashierProducts'
import jsPDF from 'jspdf'
import Swal from 'sweetalert2'
import { getUserInfoSync } from '../../config/api'

const brandColors = {
  primary: '#1338b5',
  mid: '#1f4dc7',
  bright: '#5c8ef0',
}

const categoryFilters = ['All', 'Investment', 'Business', 'Credit', 'Cards']

const Quotations = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [cartItems, setCartItems] = useState([])
  const [activeFilter, setActiveFilter] = useState(categoryFilters[0])
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    company: '',
  })
  const [quotationNumber, setQuotationNumber] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [notes, setNotes] = useState('')
  const { products, loading, error } = useCashierProducts()
  
  // Get logged in user information
  const userInfo = useMemo(() => getUserInfoSync() || null, [])
  const cashierName = useMemo(() => userInfo?.employee_name || 'Cashier', [userInfo])
  const branchName = useMemo(() => userInfo?.branch_name || 'Main Branch', [userInfo])
  const branchAddress = useMemo(() => {
    // Try to get branch address from userInfo
    return userInfo?.branch_address || userInfo?.branch?.address || branchName
  }, [userInfo, branchName])

  const branchPhone = useMemo(() => {
    const p = userInfo?.branch_phone
    if (p == null || p === '') return ''
    return String(p)
  }, [userInfo])

  // Generate quotation number on mount
  useEffect(() => {
    const date = new Date()
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
    setQuotationNumber(`QT-${year}${month}${day}-${random}`)
    
    // Set valid until date (default 30 days from now)
    const validDate = new Date()
    validDate.setDate(validDate.getDate() + 30)
    setValidUntil(validDate.toISOString().split('T')[0])
  }, [])

  const normalizedProducts = useMemo(() => {
    if (!products?.length) return []

    return products.map((item) => {
      const stock = Number(item.quantity)
      const price = Number(item.selling_price)
      let productId = null
      if (item.product !== undefined && item.product !== null) {
        productId = typeof item.product === 'object' ? item.product.id : item.product
      } else if (item.product_id !== undefined) {
        productId = item.product_id
      }

      return {
        id: item.id ?? item.product_id ?? item.product_sku,
        product_id: productId,
        name: item.product_name || 'Unnamed product',
        sku: item.product_sku || 'N/A',
        price: Number.isFinite(price) ? price : 0,
        stock: Number.isFinite(stock) ? stock : 0,
        category:
          item.category_name ||
          item.product_category ||
          item.category ||
          'General',
      }
    })
  }, [products])

  const filteredProducts = useMemo(() => {
    return normalizedProducts.filter((item) => {
      const query = searchTerm.toLowerCase()
      const matchesSearch =
        !searchTerm ||
        item.name.toLowerCase().includes(query) ||
        item.sku.toLowerCase().includes(query) ||
        item.category.toLowerCase().includes(query)

      const matchesCategory =
        activeFilter === 'All' || item.category.toLowerCase() === activeFilter.toLowerCase()

      return matchesSearch && matchesCategory
    })
  }, [searchTerm, activeFilter, normalizedProducts])

  const subtotal = useMemo(() => {
    return cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  }, [cartItems])

  const total = subtotal

  const totalProducts = filteredProducts.length
  const allProductsCount = normalizedProducts.length
  const hasInventory = allProductsCount > 0
  const showEmptyState = !loading && !error && filteredProducts.length === 0
  const showNoInventory = !loading && !error && !hasInventory

  const currency = (value) =>
    value.toLocaleString(undefined, { style: 'currency', currency: 'USD' })

  const handleAddToCart = useCallback((product) => {
    // Don't allow adding products with 0 stock
    // Note: Quotations do not deduct stock - they are just price quotes
    if (product.stock <= 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Out of Stock',
        text: 'This product is currently out of stock and cannot be added to the quotation.',
        confirmButtonColor: brandColors.primary
      })
      return
    }

    setCartItems((prev) => {
      const found = prev.find((item) => item.id === product.id)
      if (found) {
        return prev.map((item) =>
          item.id === product.id
            ? {
                ...item,
                quantity: Math.min(item.quantity + 1, product.stock),
              }
            : item
        )
      }
      return [...prev, { ...product, quantity: 1 }]
    })
  }, [])

  const handleQuantityChange = (id, value) => {
    const nextValue = Math.max(1, Number(value) || 1)
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity: Math.min(nextValue, item.stock),
            }
          : item
      )
    )
  }

  const handleRemoveFromCart = (id) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id))
  }

  const handleGenerateQuotation = async () => {
    if (cartItems.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Empty Cart',
        text: 'Please add at least one product to generate a quotation.',
        confirmButtonColor: brandColors.primary
      })
      return
    }

    if (!customerInfo.name.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Customer Information Required',
        text: 'Please enter customer name to generate a quotation.',
        confirmButtonColor: brandColors.primary
      })
      return
    }

    try {
      // Show loading
      Swal.fire({
        title: 'Generating Quotation...',
        text: 'Please wait while we create your quotation.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      // Generate PDF
      const doc = new jsPDF('portrait', 'mm', 'a4')
      const pageWidth = doc.internal.pageSize.getWidth()
      const margin = 15
      let yPos = margin

      // Beautiful Header with Decor Masters branding - Enhanced Design
      // Main header background
      doc.setFillColor(19, 56, 181)
      doc.rect(0, 0, pageWidth, 55, 'F')
      
      // Accent stripe
      doc.setFillColor(92, 142, 240)
      doc.rect(0, 0, pageWidth, 3, 'F')
      
      doc.setTextColor(255, 255, 255)
      
      // Company Name with shadow effect
      doc.setFontSize(32)
      doc.setFont('helvetica', 'bold')
      doc.text('DECOR MASTERS', pageWidth / 2, 25, { align: 'center' })
      
      // Decorative line under company name - moved down to avoid covering text
      doc.setDrawColor(255, 255, 255)
      doc.setLineWidth(0.5)
      doc.line(margin, 37, pageWidth - margin, 37)
      
      // Tagline with better styling
      doc.setFontSize(12)
      doc.setFont('helvetica', 'italic')
      doc.setTextColor(230, 230, 255)
      doc.text('Your Trusted Partner in Quality Products', pageWidth / 2, 35, { align: 'center' })
      
      // Quotation badge on left
      doc.setFillColor(255, 255, 255)
      doc.setTextColor(19, 56, 181)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.rect(margin, 40, 50, 12, 'F')
      doc.text('QUOTATION', margin + 25, 47, { align: 'center' })
      
      // Quotation Details on right with better styling
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.text(`# ${quotationNumber}`, pageWidth - margin, 40, { align: 'right' })
      doc.setFontSize(8)
      doc.text(`Date: ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, pageWidth - margin, 46, { align: 'right' })
      doc.text(`Valid: ${new Date(validUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, pageWidth - margin, 52, { align: 'right' })

      yPos = 65

      // Business Information Box - Enhanced with accent
      // Accent bar
      doc.setFillColor(19, 56, 181)
      doc.rect(margin, yPos, 4, 38, 'F')
      
      // Main box
      doc.setFillColor(250, 251, 253)
      doc.setDrawColor(200, 210, 230)
      doc.setLineWidth(0.5)
      doc.rect(margin + 4, yPos, pageWidth - 2 * margin - 4, 38, 'FD')
      
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(19, 56, 181)
      doc.text('FROM', margin + 8, yPos + 8)
      
      // Decorative line
      doc.setDrawColor(19, 56, 181)
      doc.setLineWidth(0.3)
      doc.line(margin + 8, yPos + 10, margin + 35, yPos + 10)
      
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(30, 30, 30)
      doc.text('Decor Masters', margin + 8, yPos + 17)
      
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      doc.text(branchName, margin + 8, yPos + 23)
      doc.text(branchAddress, margin + 8, yPos + 29)
      if (branchPhone) {
        doc.text(`Phone: ${branchPhone}`, margin + 8, yPos + 35)
      }
      
      // Cashier info badge on right side - ensure it fits
      doc.setFillColor(19, 56, 181)
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(7)
      doc.setFont('helvetica', 'bold')
      const cashierText = `By: ${cashierName.substring(0, 20)}`
      const textWidth = doc.getTextWidth(cashierText)
      const badgeWidth = Math.min(textWidth + 6, pageWidth - 2 * margin - 10)
      doc.rect(pageWidth - margin - badgeWidth, yPos + 28, badgeWidth, 8, 'F')
      doc.text(cashierText.substring(0, 25), pageWidth - margin - 3, yPos + 33.5, { align: 'right' })

      yPos += 48

      // Customer Information - Enhanced styling
      // Section header with accent
      doc.setFillColor(19, 56, 181)
      doc.rect(margin, yPos, pageWidth - 2 * margin, 6, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(11)
      doc.setFont('helvetica', 'bold')
      doc.text('TO', margin + 5, yPos + 4.5)
      yPos += 10

      // Customer details box - adjust height based on content
      doc.setFillColor(255, 255, 255)
      doc.setDrawColor(220, 220, 220)
      doc.setLineWidth(0.3)
      
      // Calculate required height - increased base height and better spacing
      let estimatedHeight = 30 // Increased base height
      if (customerInfo.company) {
        const companyLines = doc.splitTextToSize(`Company: ${customerInfo.company}`, pageWidth - 2 * margin - 10)
        estimatedHeight += companyLines.length * 6 + 2 // Increased spacing
      }
      if (customerInfo.email) {
        const emailLines = doc.splitTextToSize(`Email: ${customerInfo.email}`, pageWidth - 2 * margin - 10)
        estimatedHeight += emailLines.length * 6 + 2 // Increased spacing
      }
      if (customerInfo.phone) {
        estimatedHeight += 8 // Account for phone field
      }
      if (customerInfo.address) {
        const addressLines = doc.splitTextToSize(`Address: ${customerInfo.address}`, pageWidth - 2 * margin - 10)
        estimatedHeight += addressLines.length * 6 + 2 // Increased spacing
      }
      
      // Add extra padding at bottom
      estimatedHeight += 5
      const customerBoxHeight = Math.max(30, estimatedHeight)
      doc.rect(margin, yPos, pageWidth - 2 * margin, customerBoxHeight, 'FD')
      
      doc.setTextColor(0, 0, 0)
      doc.setFontSize(10)
      doc.setFont('helvetica', 'bold')
      doc.text(customerInfo.name || 'Customer', margin + 5, yPos + 8)
      
      let customerY = yPos + 8
      if (customerInfo.company) {
        customerY += 8
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        const companyText = `Company: ${customerInfo.company}`
        const companyLines = doc.splitTextToSize(companyText, pageWidth - 2 * margin - 10)
        doc.text(companyLines, margin + 5, customerY)
        customerY += companyLines.length * 6
      }
      if (customerInfo.email) {
        customerY += 8
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        const emailText = `Email: ${customerInfo.email}`
        const emailLines = doc.splitTextToSize(emailText, pageWidth - 2 * margin - 10)
        doc.text(emailLines, margin + 5, customerY)
        customerY += emailLines.length * 6
      }
      if (customerInfo.phone) {
        customerY += 8
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        doc.text(`Phone: ${customerInfo.phone}`, margin + 5, customerY)
      }
      if (customerInfo.address) {
        customerY += 8
        doc.setFontSize(9)
        doc.setTextColor(80, 80, 80)
        const addressText = `Address: ${customerInfo.address}`
        const addressLines = doc.splitTextToSize(addressText, pageWidth - 2 * margin - 10)
        doc.text(addressLines, margin + 5, customerY)
      }
      
      yPos += customerBoxHeight + 5

      yPos += 35

      // Items Table Header - Enhanced
      doc.setFillColor(19, 56, 181)
      doc.rect(margin, yPos, pageWidth - 2 * margin, 9, 'F')
      doc.setTextColor(255, 255, 255)
      doc.setFontSize(9)
      doc.setFont('helvetica', 'bold')
      doc.text('ITEM DESCRIPTION', margin + 3, yPos + 6)
      doc.text('SKU', margin + 55, yPos + 6)
      doc.text('QTY', margin + 85, yPos + 6)
      doc.text('UNIT PRICE', margin + 105, yPos + 6)
      doc.text('TOTAL', pageWidth - margin - 2, yPos + 6, { align: 'right' })
      yPos += 12

      // Items with alternating row colors
      doc.setFont('helvetica', 'normal')
      cartItems.forEach((item, index) => {
        if (yPos > 240) {
          doc.addPage()
          yPos = margin + 20
        }

        // Alternating row background
        if (index % 2 === 0) {
          doc.setFillColor(250, 251, 253)
          doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F')
        } else {
          doc.setFillColor(255, 255, 255)
          doc.rect(margin, yPos - 4, pageWidth - 2 * margin, 8, 'F')
        }

        // Row border
        doc.setDrawColor(230, 230, 230)
        doc.setLineWidth(0.2)
        doc.line(margin, yPos + 3, pageWidth - margin, yPos + 3)

        doc.setFontSize(9)
        doc.setTextColor(30, 30, 30)
        // Ensure product name fits in column
        const productName = item.name.length > 28 ? item.name.substring(0, 25) + '...' : item.name
        doc.text(productName, margin + 3, yPos)
        
        doc.setFontSize(8)
        doc.setTextColor(100, 100, 100)
        doc.text(item.sku, margin + 55, yPos)
        doc.text(item.quantity.toString(), margin + 85, yPos)
        
        doc.setTextColor(60, 60, 60)
        doc.text(currency(item.price), margin + 105, yPos)
        
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(19, 56, 181)
        doc.text(currency(item.price * item.quantity), pageWidth - margin - 2, yPos, { align: 'right' })
        yPos += 8
      })

      yPos += 5

      // Totals Section - Enhanced with box
      yPos += 5
      
      // Top border
      doc.setDrawColor(19, 56, 181)
      doc.setLineWidth(1)
      doc.line(margin, yPos, pageWidth - margin, yPos)
      
      // Total box
      doc.setFillColor(245, 247, 252)
      doc.setDrawColor(19, 56, 181)
      doc.setLineWidth(0.5)
      doc.rect(margin, yPos, pageWidth - 2 * margin, 15, 'FD')
      
      yPos += 10
      
      doc.setFontSize(12)
      doc.setFont('helvetica', 'bold')
      doc.setTextColor(19, 56, 181)
      doc.text('TOTAL AMOUNT', pageWidth - margin - 60, yPos, { align: 'right' })
      
      doc.setFontSize(18)
      doc.setTextColor(19, 56, 181)
      doc.text(currency(total), pageWidth - margin - 2, yPos, { align: 'right' })
      
      // Bottom border
      doc.setDrawColor(19, 56, 181)
      doc.setLineWidth(1)
      doc.line(margin, yPos + 5, pageWidth - margin, yPos + 5)
      
      yPos += 12

      yPos += 15

      // Notes - Enhanced styling
      if (notes) {
        const notesLines = doc.splitTextToSize(notes, pageWidth - 2 * margin - 10)
        const notesHeight = Math.max(20, notesLines.length * 5 + 15)
        
        doc.setFillColor(255, 250, 240)
        doc.setDrawColor(240, 220, 180)
        doc.setLineWidth(0.3)
        doc.rect(margin, yPos, pageWidth - 2 * margin, notesHeight, 'FD')
        
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(10)
        doc.setTextColor(19, 56, 181)
        doc.text('NOTES', margin + 5, yPos + 7)
        yPos += 10
        
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(9)
        doc.setTextColor(60, 60, 60)
        doc.text(notesLines, margin + 5, yPos)
        yPos += notesLines.length * 5 + 8
      }

      // Friendly Closing Message - Enhanced with gradient effect
      yPos += 15
      
      // Decorative top border
      doc.setFillColor(19, 56, 181)
      doc.rect(margin, yPos, pageWidth - 2 * margin, 2, 'F')
      yPos += 5
      
      // Main message box with gradient-like effect
      doc.setFillColor(250, 251, 253)
      doc.setDrawColor(19, 56, 181)
      doc.setLineWidth(0.5)
      doc.rect(margin, yPos, pageWidth - 2 * margin, 35, 'FD')
      
      // Accent stripe
      doc.setFillColor(19, 56, 181)
      doc.rect(margin, yPos, pageWidth - 2 * margin, 3, 'F')
      
      doc.setTextColor(19, 56, 181)
      doc.setFontSize(14)
      doc.setFont('helvetica', 'bold')
      doc.text('Thank You for Choosing Decor Masters!', pageWidth / 2, yPos + 12, { align: 'center' })
      
      doc.setFontSize(9)
      doc.setFont('helvetica', 'normal')
      doc.setTextColor(60, 60, 60)
      const thankYouText = [
        'We appreciate your interest in our products and services.',
        'This quotation is valid until the date specified above.',
        'Please feel free to contact us if you have any questions.',
        'Transforming spaces, Enhancing Lives,'
      ]
      thankYouText.forEach((line, index) => {
        doc.text(line, pageWidth / 2, yPos + 19 + (index * 4), { align: 'center' })
      })

      // Footer with decorative line
      const footerY = doc.internal.pageSize.getHeight() - 8
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.3)
      doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5)
      
      doc.setFontSize(8)
      doc.setTextColor(120, 120, 120)
      doc.setFont('helvetica', 'italic')
      doc.text('Decor Masters - Quality Products, Trusted Service', pageWidth / 2, footerY, { align: 'center' })

      // Save PDF
      const fileName = `Quotation_${quotationNumber}_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      // Close loading dialog first
      Swal.close()

      // Show success message
      await Swal.fire({
        icon: 'success',
        title: 'Quotation Generated!',
        text: `Quotation ${quotationNumber} has been generated and downloaded.`,
        confirmButtonColor: brandColors.primary
      })

      // Reset form
      setCartItems([])
      setCustomerInfo({
        name: '',
        email: '',
        phone: '',
        address: '',
        company: '',
      })
      setNotes('')
      const date = new Date()
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0')
      setQuotationNumber(`QT-${year}${month}${day}-${random}`)

    } catch (error) {
      console.error('Error generating quotation:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate quotation. Please try again.',
        confirmButtonColor: brandColors.primary
      })
    }
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Generate Quotation</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="/">Home</a>
            </li>
            <li className="breadcrumb-item active">Quotation</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body p-4">
                <div
                  className="d-flex justify-content-between align-items-center flex-wrap mb-4"
                  style={{
                    paddingBottom: '16px',
                    borderBottom: '2px solid #eef1f4',
                  }}
                >
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-file-text me-2" />
                      Select Products
                    </h5>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <span
                        className="badge rounded-pill"
                        style={{
                          background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.bright} 100%)`,
                          color: '#fff',
                          padding: '6px 14px',
                          fontSize: '13px',
                          boxShadow: '0 2px 6px rgba(19, 56, 181, 0.25)',
                        }}
                      >
                        Total: <strong className="ms-1">{totalProducts}</strong>
                      </span>
                      <span
                        className="badge rounded-pill bg-light text-muted border"
                        style={{ padding: '6px 14px', fontSize: '13px' }}
                      >
                        Cart items: <strong className="ms-1">{cartItems.length}</strong>
                      </span>
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-2 gap-lg-3 mb-3">
                  <div className="flex-grow-1 w-100">
                    <div
                      className="position-relative"
                      style={{
                        borderRadius: 999,
                        background: '#f7f9fc',
                        boxShadow: 'inset 0 0 0 1px rgba(19, 56, 181, 0.1)',
                      }}
                    >
                      <i
                        className="bi bi-search text-muted position-absolute"
                        style={{ left: 16, top: '50%', transform: 'translateY(-50%)' }}
                      />
                      <input
                        type="search"
                        className="form-control border-0 bg-transparent"
                        style={{
                          padding: '10px 16px 10px 40px',
                          borderRadius: 999,
                          fontSize: 14,
                        }}
                        placeholder="Search products quickly..."
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3">
                  {categoryFilters.map((category) => {
                    const isActive = activeFilter === category
                    return (
                      <button
                        key={category}
                        type="button"
                        className="btn btn-sm rounded-pill"
                        style={{
                          background: isActive ? `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.bright} 100%)` : '#f5f7fb',
                          color: isActive ? '#fff' : '#4a4f5f',
                          boxShadow: isActive ? '0 4px 12px rgba(19, 56, 181, 0.25)' : 'none',
                          border: 'none',
                          paddingInline: '16px',
                        }}
                        onClick={() => setActiveFilter(category)}
                      >
                        {category}
                      </button>
                    )
                  })}
                </div>

                {loading && (
                  <div className="alert alert-light border text-center mb-0">
                    <div className="d-inline-flex align-items-center gap-2">
                      <div className="spinner-border text-primary" role="status" />
                      <span className="fw-semibold text-muted">Loading products…</span>
                    </div>
                  </div>
                )}

                {!loading && error && (
                  <div className="alert alert-danger text-center mb-0" role="alert">
                    {error}
                  </div>
                )}

                {showNoInventory && (
                  <div className="alert alert-light border text-center mb-0">
                    No branch stock is available yet. Please check back later.
                  </div>
                )}

                {!loading && !error && hasInventory && (
                  <div className="list-group list-group-flush">
                    {filteredProducts.map((product, index) => (
                      <div
                        key={product.id}
                        className={`list-group-item px-0 py-3 ${
                          index !== filteredProducts.length - 1 ? 'border-bottom' : ''
                        }`}
                      >
                        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3">
                          <div className="d-flex align-items-start gap-3">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                width: 44,
                                height: 44,
                                backgroundColor: 'rgba(19, 56, 181, 0.1)',
                                color: brandColors.primary,
                              }}
                            >
                              <span className="fw-semibold">
                                {product.name.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <h6 className="mb-0">{product.name}</h6>
                                <span className="badge bg-light text-dark border">
                                  {product.category}
                                </span>
                              </div>
                              <div className="d-flex flex-wrap align-items-center gap-3 small text-muted">
                                <span>
                                  SKU <span className="fw-semibold">{product.sku}</span>
                                </span>
                                <span>
                                  Stock{' '}
                                  {product.stock <= 0 ? (
                                    <span className="badge bg-danger text-white rounded-pill">
                                      Out of Stock
                                    </span>
                                  ) : product.stock <= 6 ? (
                                    <span className="badge bg-danger-subtle text-danger rounded-pill">
                                      {product.stock} units
                                    </span>
                                  ) : (
                                    <span
                                      className="badge rounded-pill"
                                      style={{
                                        backgroundColor: 'rgba(19, 56, 181, 0.12)',
                                        color: brandColors.primary,
                                      }}
                                    >
                                      {product.stock} units
                                    </span>
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="text-md-end ms-md-auto d-flex flex-column align-items-md-end gap-2">
                            <div>
                              <p className="mb-1 text-muted small">Unit Price</p>
                              <p className="fw-bold fs-5 mb-0">{currency(product.price)}</p>
                            </div>
                            <button
                              className="btn btn-sm rounded-pill px-3 text-white"
                              style={{
                                background: product.stock > 0 
                                  ? `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`
                                  : '#cccccc',
                                boxShadow: product.stock > 0 
                                  ? '0 8px 18px rgba(19, 56, 181, 0.25)'
                                  : 'none',
                                border: 'none',
                                cursor: product.stock > 0 ? 'pointer' : 'not-allowed',
                              }}
                              onClick={() => handleAddToCart(product)}
                              disabled={product.stock <= 0}
                            >
                              <i className="bi bi-plus-circle me-1" />
                              {product.stock > 0 ? 'Add to cart' : 'Out of Stock'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {showEmptyState && (
                      <div className="list-group-item px-0 py-4">
                        <div className="alert alert-light border text-center mb-0">
                          No products match "{searchTerm}". Try a different keyword.
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                background:
                  'linear-gradient(180deg, rgba(19,56,181,0.04) 0%, rgba(255,255,255,1) 45%)',
              }}
            >
              <div className="card-body p-3 p-lg-4 d-flex flex-column">
                <div
                  className="d-flex align-items-center justify-content-between pb-2 mb-3"
                  style={{ borderBottom: '1px dashed rgba(19,56,181,0.2)' }}
                >
                  <div>
                    <h6 className="mb-1 text-uppercase text-muted" style={{ letterSpacing: '0.08em' }}>
                      Quotation Details
                    </h6>
                    <p className="text-muted small mb-0">
                      {cartItems.length} item{cartItems.length !== 1 && 's'} in cart
                    </p>
                  </div>
                </div>

                {/* Customer Information */}
                <div className="mb-3">
                  <h6 className="small fw-bold text-uppercase text-muted mb-2">Customer Information</h6>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Customer Name *</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                      placeholder="Enter customer name"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Company</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={customerInfo.company}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, company: e.target.value })}
                      placeholder="Company name (optional)"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Email</label>
                    <input
                      type="email"
                      className="form-control form-control-sm"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Phone</label>
                    <input
                      type="tel"
                      className="form-control form-control-sm"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      placeholder="Phone number"
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Address</label>
                    <textarea
                      className="form-control form-control-sm"
                      rows="2"
                      value={customerInfo.address}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                      placeholder="Customer address"
                    />
                  </div>
                </div>

                {/* Quotation Settings */}
                <div className="mb-3">
                  <h6 className="small fw-bold text-uppercase text-muted mb-2">Quotation Settings</h6>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Quotation Number</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={quotationNumber}
                      onChange={(e) => setQuotationNumber(e.target.value)}
                    />
                  </div>
                  <div className="mb-2">
                    <label className="form-label small mb-1">Valid Until</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                    />
                  </div>
                </div>

                {/* Cart Items */}
                <div className="flex-grow-1 overflow-auto mb-3" style={{ maxHeight: 200 }}>
                  {cartItems.length === 0 && (
                    <div className="alert alert-light border text-center mb-0 small">
                      Your cart is empty. Browse products to get started.
                    </div>
                  )}

                  {cartItems.map((item) => (
                    <div
                      key={item.id}
                      className="rounded-4 border mb-2 p-2"
                      style={{
                        backgroundColor: '#fff',
                        boxShadow: '0 4px 12px rgba(19,56,181,0.05)',
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div
                          className="rounded-circle text-uppercase fw-bold d-flex align-items-center justify-content-center"
                          style={{
                            width: 36,
                            height: 36,
                            backgroundColor: 'rgba(19,56,181,0.12)',
                            color: brandColors.primary,
                            fontSize: 14,
                          }}
                        >
                          {item.name?.charAt(0)}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <p className="fw-semibold mb-0" style={{ fontSize: 14 }}>
                                {item.name}
                              </p>
                              <small className="text-muted">SKU {item.sku}</small>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-link p-0 text-danger text-decoration-none"
                              onClick={() => handleRemoveFromCart(item.id)}
                            >
                              Remove
                            </button>
                          </div>
                          <div className="d-flex align-items-center gap-2 mt-2">
                            <input
                              type="number"
                              className="form-control form-control-sm"
                              style={{ maxWidth: 70 }}
                              min="1"
                              value={item.quantity}
                              onChange={(event) =>
                                handleQuantityChange(item.id, event.target.value)
                              }
                            />
                            <small className="text-muted">of {item.stock} in stock</small>
                            <div className="ms-auto text-end">
                              <p className="text-muted small mb-0">Line total</p>
                              <p className="fw-bold mb-0" style={{ fontSize: 15 }}>
                                {currency(item.price * item.quantity)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>


                {/* Notes */}
                <div className="mb-3">
                  <label className="form-label small mb-1">Notes</label>
                  <textarea
                    className="form-control form-control-sm"
                    rows="3"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Additional notes or terms..."
                  />
                </div>

                {/* Totals */}
                <div className="border-top pt-3 mt-3">
                  <div className="d-flex justify-content-between mb-3">
                    <span className="fw-bold">Total Amount</span>
                    <span className="fw-bold fs-5">{currency(total)}</span>
                  </div>
                  <button
                    className="btn w-100 mt-2 text-white py-2"
                    type="button"
                    style={{
                      background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.mid} 100%)`,
                      border: 'none',
                      borderRadius: 999,
                      fontWeight: 600,
                      boxShadow: cartItems.length === 0 ? 'none' : '0 10px 20px rgba(19, 56, 181, 0.25)',
                    }}
                    disabled={cartItems.length === 0}
                    onClick={handleGenerateQuotation}
                  >
                    <i className="bi bi-file-earmark-pdf me-2" />
                    Generate Quotation PDF
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Quotations

